/**
 * Pulse — geolocation performance analytics (Owl `GetPulseGeoDiagram`). Latency
 * or availability over time, sourced from RUM or synthetic edge probing. Two tabs
 * (Static / Dynamic) toggle the `data_type`; the same 7/14/30-day range filter as
 * the Digital Experience page, plus metric / source / country-region-city filters.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Radar, X } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, SegmentedControl, Button } from '@vnetwork/ui/primitives';
import { useWafPulseGeoDiagram, useWafPlatforms } from '@/api/waf';
import { ChartCard, RefreshButton, Stack } from '../../_shared/AnalyticsKit';
import { formatNum, formatPct } from '../../_shared/analytics-format';
import { WafRangeControl, type ResolvedRange } from './WafRangeControl';
import { pulseIntervalForRange, pulseRows } from './diagram-transform';
import { loadJSON, saveJSON } from './storage';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import { WafGeoFilter, geoFilterToApi } from '../_shared/WafGeoFilter';
import { WafPlatformFilterDialog, EXTRA_PLATFORM_LABELS, defaultFamousPlatformIds } from './WafPlatformFilterDialog';
import styles from './WafAnalytics.module.css';

type DataType = 'static' | 'dynamic';

const METRIC_ITEMS = [
  { value: 'latency', label: 'Latency' },
  { value: 'availability', label: 'Availability' },
];
const SOURCE_OPTIONS: MultiSelectOption[] = [
  { value: 'rum', label: 'RUM' },
  { value: 'waver', label: 'Synthetic' },
];

export function WafPulseAnalytics({ serviceId }: { serviceId: string }) {
  const tabKey = `waf:${serviceId}:pulse:tab`;

  const [dataType, setDataType] = useState<DataType>(() => loadJSON<DataType>(tabKey, 'static'));
  const [range, setRange] = useState<ResolvedRange | null>(null);
  const [metric, setMetric] = useState('latency');
  const [sources, setSources] = useState<string[]>(['rum', 'waver']);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');

  const interval = range ? pulseIntervalForRange(range.start_time, range.end_time) : 86400;

  const platformsQ = useWafPlatforms(serviceId);
  const platformLabel = useMemo(() => {
    const map = new Map((platformsQ.data ?? []).map((p) => [p.id, p.display_name || p.name]));
    return (id: string) => (id === '0' ? 'Origin' : map.get(id) ?? EXTRA_PLATFORM_LABELS[id] ?? id);
  }, [platformsQ.data]);

  // Default the platform selection to ~10 famous CDNs (incl. VNCDN) once the
  // catalog loads, each visit.
  const seededPlatforms = useRef(false);
  useEffect(() => {
    if (seededPlatforms.current || platformsQ.isLoading) return;
    seededPlatforms.current = true;
    setPlatforms(defaultFamousPlatformIds(platformsQ.data ?? []));
  }, [platformsQ.isLoading, platformsQ.data]);

  // Composite region/city keys → flat API codes (parent country/region inferred).
  const apiGeo = useMemo(() => geoFilterToApi(country, region, city), [country, region, city]);

  const q = useWafPulseGeoDiagram(
    serviceId,
    {
      start_time: range?.start_time ?? 0,
      end_time: range?.end_time ?? 0,
      interval,
      types: metric,
      sources: sources.length ? sources.join(',') : undefined,
      data_type: dataType,
      platforms: platforms.length ? platforms.join(',') : undefined,
      country: apiGeo.country || undefined,
      region: apiGeo.region || undefined,
      city: apiGeo.city || undefined,
    },
    !!range,
  );

  const { rows, series } = useMemo(
    () => pulseRows(q.data, interval, platformLabel, metric),
    [q.data, interval, platformLabel, metric],
  );

  const changeTab = (v: DataType) => { setDataType(v); saveJSON(tabKey, v); };

  const isLatency = metric === 'latency';
  const valueFormatter = (v: string | number | undefined) =>
    isLatency ? `${formatNum(Number(v))} ms` : formatPct(Number(v), '0..100');

  return (
    <Container size="xl">
      <PageHeader
        icon={<Radar size={22} />}
        title="Pulse"
        description="Geolocation latency & availability over time, from RUM or synthetic monitoring."
      />

      <Tabs value={dataType} onValueChange={(v) => changeTab(v as DataType)}>
        <TabsList>
          <TabsTrigger value="static">Static</TabsTrigger>
          <TabsTrigger value="dynamic">Dynamic</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className={styles.toolbar} style={{ marginTop: 'var(--vn-space-3)' }}>
        <div className={styles.controls}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <WafRangeControl serviceId={serviceId} pageKey="pulse" onChange={setRange} />
            <SegmentedControl aria-label="Metric" size="md" items={METRIC_ITEMS} value={metric} onValueChange={setMetric} />
            <div style={{ width: 160 }}>
              <MultiSelect placeholder="Sources" options={SOURCE_OPTIONS} value={sources} onChange={setSources} />
            </div>
            <WafPlatformFilterDialog serviceId={serviceId} value={platforms} onChange={setPlatforms} />
          </div>
          <div className={styles.controlsRight}>
            <RefreshButton onRefresh={() => q.refetch()} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <WafGeoFilter
            country={country}
            region={region}
            city={city}
            setCountry={setCountry}
            setRegion={setRegion}
            setCity={setCity}
            showNetwork={false}
            singleLevel
          />
          {(country || region || city) && (
            <Button
              variant="ghost"
              startIcon={<X size={15} />}
              onClick={() => {
                setCountry('');
                setRegion('');
                setCity('');
              }}
              style={{ marginBottom: 2 }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </div>

      <Stack>
        <ChartCard
          title={isLatency ? 'Latency' : 'Availability'}
          description={isLatency ? 'Edge latency over the selected range.' : 'Availability over the selected range.'}
          data={rows}
          xKey="t"
          series={series}
          type="line"
          height={280}
          showLegend
          valueFormatter={valueFormatter}
          loading={q.isLoading}
        />
      </Stack>
    </Container>
  );
}

export default WafPulseAnalytics;
